import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Button from '../../../components/ui/Button';
import Card, { CardContent } from '../../../components/ui/Card';
import Modal, {
  ModalHeader,
  ModalContent,
  ModalFooter,
} from '../../../components/ui/Modal';
import FormInput from '../../../components/ui/FormInput';
import { teamMembersService, type TeamMember, type InviteTeamMemberPayload, type TeamMemberRole } from '../../../services/teamMembersService';
import { creatorService } from '../../../services/creatorService';

const ROLE_LABELS: Record<TeamMemberRole, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  EDITOR: 'Editor',
  VIEWER: 'Viewer',
};

const ROLE_DESCRIPTIONS: Record<TeamMemberRole, string> = {
  OWNER: 'Full access to all features',
  ADMIN: 'Manage team members and settings',
  EDITOR: 'Create and edit content',
  VIEWER: 'View-only access',
};

const TeamMembersPage: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [formData, setFormData] = useState<InviteTeamMemberPayload>({
    email: '',
    role: 'VIEWER',
    permissions: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [creatorId, setCreatorId] = useState<string | null>(null);

  useEffect(() => {
    loadCreatorProfile();
  }, []);

  useEffect(() => {
    if (creatorId) {
      loadTeamMembers();
    }
  }, [creatorId]);

  const loadCreatorProfile = async () => {
    try {
      const res = await creatorService.getMyCreatorProfile();
      if (res.creatorProfile?.id) {
        setCreatorId(res.creatorProfile.id);
      }
    } catch (error) {
      console.error('Failed to load creator profile:', error);
    }
  };

  const loadTeamMembers = async () => {
    if (!creatorId) return;
    try {
      setLoading(true);
      const res = await teamMembersService.getTeamMembers(creatorId);
      setTeamMembers(res.teamMembers || []);
    } catch (error: any) {
      console.error('Failed to load team members:', error);
      alert(error?.response?.data?.message || 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = () => {
    setFormData({
      email: '',
      role: 'VIEWER',
      permissions: [],
    });
    setSelectedMember(null);
    setShowInviteModal(true);
  };

  const handleEdit = (member: TeamMember) => {
    setFormData({
      email: member.user?.email || '',
      role: member.role,
      permissions: member.permissions || [],
    });
    setSelectedMember(member);
    setShowEditModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to remove this team member?')) return;

    try {
      await teamMembersService.removeTeamMember(id);
      await loadTeamMembers();
      alert('Team member removed successfully');
    } catch (error: any) {
      console.error('Failed to remove team member:', error);
      alert(error?.response?.data?.message || 'Failed to remove team member');
    }
  };

  const handleSubmit = async () => {
    if (!formData.email) {
      alert('Email is required');
      return;
    }

    setIsSubmitting(true);
    try {
      if (selectedMember) {
        await teamMembersService.updateTeamMember(selectedMember.id, {
          role: formData.role,
          permissions: formData.permissions,
        });
        alert('Team member updated successfully');
      } else {
        await teamMembersService.inviteTeamMember(formData);
        alert('Team member invited successfully');
      }
      setShowInviteModal(false);
      setShowEditModal(false);
      await loadTeamMembers();
    } catch (error: any) {
      console.error('Failed to save team member:', error);
      alert(error?.response?.data?.message || 'Failed to save team member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleColor = (role: TeamMemberRole) => {
    const colors: Record<TeamMemberRole, string> = {
      OWNER: 'bg-purple-100 text-purple-700',
      ADMIN: 'bg-blue-100 text-blue-700',
      EDITOR: 'bg-green-100 text-green-700',
      VIEWER: 'bg-gray-100 text-gray-700',
    };
    return colors[role];
  };

  const getRoleIcon = (role: TeamMemberRole) => {
    if (role === 'OWNER' || role === 'ADMIN') {
      return <ShieldCheckIcon className="w-5 h-5" />;
    }
    return <UserIcon className="w-5 h-5" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Members</h1>
          <p className="text-gray-600 mt-1">Manage your team members and their permissions</p>
        </div>
        <Button onClick={handleInvite} leftIcon={<PlusIcon className="w-5 h-5" />}>
          Invite Member
        </Button>
      </div>

      {/* Team Members List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="text-gray-400">Loading team members...</div>
        </div>
      ) : teamMembers.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No team members yet</h3>
            <p className="text-gray-600 mb-4">Invite team members to collaborate on your creator profile</p>
            <Button onClick={handleInvite} leftIcon={<PlusIcon className="w-5 h-5" />}>
              Invite Member
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teamMembers.map((member) => (
            <Card key={member.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {member.user?.avatar ? (
                      <img
                        src={member.user.avatar}
                        alt={member.user.username}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-patreon-orange rounded-full flex items-center justify-center text-white font-semibold">
                        {member.user?.username?.charAt(0).toUpperCase() || '?'}
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {member.user?.username || member.user?.email || 'Unknown'}
                      </h3>
                      {member.user?.email && (
                        <p className="text-sm text-gray-500">{member.user.email}</p>
                      )}
                    </div>
                  </div>
                  {!member.isActive && (
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                      Inactive
                    </span>
                  )}
                </div>

                <div className="mb-4">
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getRoleColor(member.role)}`}>
                    {getRoleIcon(member.role)}
                    <span>{ROLE_LABELS[member.role]}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">{ROLE_DESCRIPTIONS[member.role]}</p>
                </div>

                {member.permissions && member.permissions.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-1">Custom Permissions:</p>
                    <div className="flex flex-wrap gap-1">
                      {member.permissions.map((permission) => (
                        <span
                          key={permission}
                          className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                        >
                          {permission}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(member)}
                    leftIcon={<PencilIcon className="w-4 h-4" />}
                  >
                    Edit
                  </Button>
                  {member.role !== 'OWNER' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(member.id)}
                      leftIcon={<TrashIcon className="w-4 h-4" />}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Invite/Edit Modal */}
      {(showInviteModal || showEditModal) && (
        <Modal
          isOpen={showInviteModal || showEditModal}
          onClose={() => {
            setShowInviteModal(false);
            setShowEditModal(false);
          }}
        >
          <ModalHeader>
            {selectedMember ? 'Edit Team Member' : 'Invite Team Member'}
          </ModalHeader>
          <ModalContent>
            <div className="space-y-4">
              <FormInput
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="user@example.com"
                required
                disabled={!!selectedMember}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as TeamMemberRole })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                >
                  {Object.entries(ROLE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label} - {ROLE_DESCRIPTIONS[value as TeamMemberRole]}
                    </option>
                  ))}
                </select>
              </div>

              {formData.permissions && formData.permissions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Permissions
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {formData.permissions.map((permission, index) => (
                      <div key={index} className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full">
                        <span className="text-sm text-gray-700">{permission}</span>
                        <button
                          onClick={() => {
                            const newPermissions = formData.permissions?.filter((_, i) => i !== index) || [];
                            setFormData({ ...formData, permissions: newPermissions });
                          }}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ModalContent>
          <ModalFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowInviteModal(false);
                setShowEditModal(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isSubmitting}>
              {selectedMember ? 'Update' : 'Invite'}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

export default TeamMembersPage;


